import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Modal, Select, Skeleton, Tooltip, Row, Col } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  TeamOutlined,
  ProjectOutlined,
  UserDeleteOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { taskService } from '../../services/taskService';
import { SectionCard, StatTile, EmptyState, FilterChips, PersonAvatar } from '../ui';
import './TasksDayView.css';

export interface DayEmployee {
  id: number;
  name: string;
  surname?: string;
  thumbnailUrl?: string | null;
  imageUrl?: string | null;
}

export interface DayProject {
  id: number;
  name: string;
  status?: string;
  location?: string | null;
}

export interface DayAssignment {
  id: number;
  employee_id: number;
  project_id: number;
  date: string;
  employee?: DayEmployee;
  project?: DayProject;
}

export type DayGroupBy = 'employee' | 'project';

const personName = (employee?: DayEmployee) =>
  employee ? `${employee.name} ${employee.surname ?? ''}`.trim() : '—';

interface Props {
  /** YYYY-MM-DD */
  date: string;
  assignments: DayAssignment[];
  employees: DayEmployee[];
  projects: DayProject[];
  loading: boolean;
  colorFor: (projectId: number) => string;
  onDateChange: (date: string) => void;
  onEdit: (assignment: DayAssignment) => void;
  onOpenMonth: (mode: 'employee' | 'project', id: number) => void;
  onAssigned: () => void;
  /** Bumped by the page's "Assign task" button to open the assign dialog. */
  assignRequest: number;
}

/**
 * Who works on which project on one day — web twin of the mobile admin's Day view
 * (Estethica-mobile/components/admin/TasksDayView.tsx).
 */
const TasksDayView: React.FC<Props> = ({
  date,
  assignments,
  employees,
  projects,
  loading,
  colorFor,
  onDateChange,
  onEdit,
  onOpenMonth,
  onAssigned,
  assignRequest,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [groupBy, setGroupBy] = useState<DayGroupBy>('employee');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignEmployee, setAssignEmployee] = useState<number | undefined>();
  const [assignProject, setAssignProject] = useState<number | undefined>();
  const [assigning, setAssigning] = useState(false);

  const current = dayjs(date);
  const today = dayjs().format('YYYY-MM-DD');
  const relative =
    date === today
      ? t('tasks.today')
      : date === dayjs().add(1, 'day').format('YYYY-MM-DD')
        ? t('tasks.tomorrow')
        : date === dayjs().subtract(1, 'day').format('YYYY-MM-DD')
          ? t('tasks.yesterday')
          : null;
  const dayTitle = relative ?? current.toDate().toLocaleDateString(locale, { weekday: 'long' });
  const fullDate = current.toDate().toLocaleDateString(locale, {
    weekday: relative ? 'long' : undefined,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // Monday-first week around the selected day
  const week = useMemo(() => {
    const monday = current.subtract((current.day() + 6) % 7, 'day');
    return Array.from({ length: 7 }, (_, index) => monday.add(index, 'day'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const byEmployee = useMemo(() => {
    const groups = new Map<number, { employee?: DayEmployee; items: DayAssignment[] }>();
    assignments.forEach((assignment) => {
      const group = groups.get(assignment.employee_id) ?? { employee: assignment.employee, items: [] };
      group.items.push(assignment);
      groups.set(assignment.employee_id, group);
    });
    return Array.from(groups.values()).sort((a, b) => personName(a.employee).localeCompare(personName(b.employee)));
  }, [assignments]);

  const byProject = useMemo(() => {
    const groups = new Map<number, { project?: DayProject; items: DayAssignment[] }>();
    assignments.forEach((assignment) => {
      const group = groups.get(assignment.project_id) ?? { project: assignment.project, items: [] };
      group.items.push(assignment);
      groups.set(assignment.project_id, group);
    });
    groups.forEach((group) => group.items.sort((a, b) => personName(a.employee).localeCompare(personName(b.employee))));
    return Array.from(groups.values()).sort((a, b) => (a.project?.name ?? '').localeCompare(b.project?.name ?? ''));
  }, [assignments]);

  const unassigned = useMemo(() => {
    const assigned = new Set(assignments.map((assignment) => assignment.employee_id));
    return employees
      .filter((employee) => !assigned.has(employee.id))
      .sort((a, b) => personName(a).localeCompare(personName(b)));
  }, [assignments, employees]);

  // ---------- Assign for this day ----------
  const openAssign = (employeeId?: number) => {
    setAssignEmployee(employeeId);
    setAssignProject(undefined);
    setAssignOpen(true);
  };

  // Only react to new clicks, not to a count left over from before this view was mounted
  const lastAssignRequest = useRef(assignRequest);
  useEffect(() => {
    if (assignRequest !== lastAssignRequest.current) {
      lastAssignRequest.current = assignRequest;
      openAssign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignRequest]);

  const projectsOf = (employeeId: number) =>
    assignments.filter((assignment) => assignment.employee_id === employeeId);

  // People without an assignment first; for the others, say where they already are
  const employeeOptions = useMemo(() => {
    const busy = new Set(assignments.map((assignment) => assignment.employee_id));
    return [...employees]
      .sort((a, b) => Number(busy.has(a.id)) - Number(busy.has(b.id)) || personName(a).localeCompare(personName(b)))
      .map((employee) => {
        const already = projectsOf(employee.id).map((assignment) => assignment.project?.name ?? '—');
        return {
          value: employee.id,
          label: personName(employee),
          already: already.length ? t('tasks.alreadyOn', { projects: already.join(', ') }) : undefined,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, assignments, t]);

  // Active projects the chosen person doesn't already have that day
  const projectOptions = projects
    .filter(
      (project) =>
        project.status === 'active' &&
        !assignments.some(
          (assignment) => assignment.employee_id === assignEmployee && assignment.project_id === project.id,
        ),
    )
    .map((project) => ({ value: project.id, label: project.name }));

  const handleAssign = async () => {
    if (!assignEmployee || !assignProject) return;
    try {
      setAssigning(true);
      await taskService.assignEmployeeCalendar(String(assignProject), {
        employee_id: assignEmployee,
        dates: [date],
      });
      toast.success(t('tasks.assignedForDay'));
      setAssignOpen(false);
      onAssigned();
    } catch (error: any) {
      const data = error?.response?.data;
      const firstError = data?.errors ? (Object.values(data.errors)[0] as string[])?.[0] : undefined;
      toast.error(firstError || data?.message || t('tasks.assignFailed'));
    } finally {
      setAssigning(false);
    }
  };

  // ---------- Rendering ----------
  const projectRow = (assignment: DayAssignment) => (
    <button type="button" key={assignment.id} className="tdv-row" onClick={() => onEdit(assignment)}>
      <span className="tdv-stripe" style={{ background: colorFor(assignment.project_id) }} aria-hidden />
      <span className="tdv-row-body">
        <span className="tdv-row-title">{assignment.project?.name ?? '—'}</span>
        {assignment.project?.location ? (
          <span className="tdv-row-sub">
            <EnvironmentOutlined /> {assignment.project.location}
          </span>
        ) : null}
      </span>
      <EditOutlined className="tdv-row-edit" aria-label={t('tasks.editAssignment')} />
    </button>
  );

  const employeeRow = (assignment: DayAssignment) => (
    <button type="button" key={assignment.id} className="tdv-row" onClick={() => onEdit(assignment)}>
      <PersonAvatar
        name={assignment.employee?.name}
        surname={assignment.employee?.surname}
        imageUrl={assignment.employee?.thumbnailUrl}
        size={30}
      />
      <span className="tdv-row-body">
        <span className="tdv-row-title">{personName(assignment.employee)}</span>
      </span>
      <EditOutlined className="tdv-row-edit" aria-label={t('tasks.editAssignment')} />
    </button>
  );

  const monthButton = (mode: 'employee' | 'project', id: number) => (
    <Tooltip title={t('tasks.openMonth')}>
      <Button
        type="text"
        className="tdv-month-button"
        icon={<CalendarOutlined />}
        onClick={() => onOpenMonth(mode, id)}
        aria-label={t('tasks.openMonth')}
      />
    </Tooltip>
  );

  return (
    <>
      {/* Day navigation */}
      <div className="e22-card tdv-daybar">
        <div className="tdv-stepper">
          <Tooltip title={t('tasks.previousDay')}>
            <Button
              icon={<LeftOutlined />}
              onClick={() => onDateChange(current.subtract(1, 'day').format('YYYY-MM-DD'))}
              aria-label={t('tasks.previousDay')}
            />
          </Tooltip>
          <div className="tdv-title">
            <div className="tdv-title-main">{dayTitle}</div>
            <div className="tdv-title-sub">{fullDate}</div>
          </div>
          <Tooltip title={t('tasks.nextDay')}>
            <Button
              icon={<RightOutlined />}
              onClick={() => onDateChange(current.add(1, 'day').format('YYYY-MM-DD'))}
              aria-label={t('tasks.nextDay')}
            />
          </Tooltip>
        </div>

        <div className="tdv-week" role="radiogroup" aria-label={t('tasks.byDay')}>
          {week.map((day) => {
            const key = day.format('YYYY-MM-DD');
            const selected = key === date;
            return (
              <button
                type="button"
                key={key}
                role="radio"
                aria-checked={selected}
                aria-label={day.toDate().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                className={[
                  'tdv-weekday',
                  selected ? 'tdv-weekday--selected' : '',
                  key === today ? 'tdv-weekday--today' : '',
                ].join(' ')}
                onClick={() => onDateChange(key)}
              >
                <span className="tdv-weekday-name">
                  {day.toDate().toLocaleDateString(locale, { weekday: 'short' }).replace('.', '').slice(0, 2)}
                </span>
                <span className="tdv-weekday-number">{day.format('D')}</span>
              </button>
            );
          })}
        </div>

        <div className="tdv-jump">
          {date !== today ? (
            <button type="button" className="e22-link-button" onClick={() => onDateChange(today)}>
              {t('tasks.backToToday')}
            </button>
          ) : null}
          <DatePicker
            value={current}
            allowClear={false}
            format="DD.MM.YYYY"
            onChange={(value) => value && onDateChange(value.format('YYYY-MM-DD'))}
            placeholder={t('tasks.pickDate')}
            style={{ width: 140 }}
          />
        </div>
      </div>

      {/* Summary */}
      <Row gutter={[16, 16]} className="tdv-stats">
        <Col xs={8}>
          <StatTile label={t('tasks.statWorking')} value={byEmployee.length} icon={<TeamOutlined />} loading={loading} />
        </Col>
        <Col xs={8}>
          <StatTile
            label={t('tasks.statProjects')}
            value={byProject.length}
            icon={<ProjectOutlined />}
            tone="violet"
            loading={loading}
          />
        </Col>
        <Col xs={8}>
          <StatTile
            label={t('tasks.statUnassigned')}
            value={unassigned.length}
            icon={<UserDeleteOutlined />}
            tone={unassigned.length > 0 ? 'warning' : 'neutral'}
            loading={loading}
          />
        </Col>
      </Row>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={16}>
          <SectionCard
            title={t('tasks.dayAssignments')}
            icon={<ScheduleOutlined />}
            subtitle={loading ? undefined : t('tasks.assignmentCount', { count: assignments.length })}
            action={
              <FilterChips<DayGroupBy>
                ariaLabel={t('tasks.dayAssignments')}
                value={groupBy}
                onChange={setGroupBy}
                options={[
                  { key: 'employee', label: t('tasks.groupEmployees'), count: loading ? undefined : byEmployee.length },
                  { key: 'project', label: t('tasks.groupProjects'), count: loading ? undefined : byProject.length },
                ]}
              />
            }
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : assignments.length === 0 ? (
              <EmptyState
                icon={<CalendarOutlined />}
                text={`${t('tasks.nothingThisDay')} · ${t('tasks.nothingThisDayHint')}`}
                action={
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => openAssign()}>
                    {t('tasks.assignForDay')}
                  </Button>
                }
              />
            ) : (
              <div className="tdv-groups">
                {groupBy === 'employee'
                  ? byEmployee.map(({ employee, items }) => (
                      <div className="tdv-group" key={items[0].employee_id}>
                        <div className="tdv-group-head">
                          <PersonAvatar
                            name={employee?.name}
                            surname={employee?.surname}
                            imageUrl={employee?.thumbnailUrl}
                            size={38}
                          />
                          <div className="tdv-row-body">
                            <div className="tdv-group-title">{personName(employee)}</div>
                            <div className="tdv-group-sub">{t('tasks.projectCount', { count: items.length })}</div>
                          </div>
                          {monthButton('employee', items[0].employee_id)}
                        </div>
                        <div className="tdv-group-body">{items.map(projectRow)}</div>
                      </div>
                    ))
                  : byProject.map(({ project, items }) => {
                      const color = colorFor(items[0].project_id);
                      return (
                        <div className="tdv-group" key={items[0].project_id}>
                          <div className="tdv-group-head">
                            <div className="tdv-project-icon" style={{ background: `${color}1f`, color }} aria-hidden>
                              <ProjectOutlined />
                            </div>
                            <div className="tdv-row-body">
                              <div className="tdv-group-title">{project?.name ?? '—'}</div>
                              <div className="tdv-group-sub">
                                {t('tasks.peopleCount', { count: items.length })}
                                {project?.location ? ` · ${project.location}` : ''}
                              </div>
                            </div>
                            {monthButton('project', items[0].project_id)}
                          </div>
                          <div className="tdv-group-body">{items.map(employeeRow)}</div>
                        </div>
                      );
                    })}
              </div>
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={8}>
          <SectionCard
            title={t('tasks.notAssigned')}
            icon={<UserDeleteOutlined />}
            subtitle={loading ? undefined : unassigned.length > 0 ? t('tasks.clickToAssign') : t('tasks.everyoneAssigned')}
            action={
              <Button icon={<PlusOutlined />} onClick={() => openAssign()} disabled={loading}>
                {t('tasks.assignForDay')}
              </Button>
            }
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 4 }} title={false} />
            ) : unassigned.length === 0 ? (
              <EmptyState icon={<TeamOutlined />} text={t('tasks.everyoneAssigned')} />
            ) : (
              <div className="tdv-chips">
                {unassigned.map((employee) => (
                  <button
                    type="button"
                    key={employee.id}
                    className="tdv-chip"
                    onClick={() => openAssign(employee.id)}
                    aria-label={`${t('tasks.assignTask')}: ${personName(employee)}`}
                  >
                    <PersonAvatar
                      name={employee.name}
                      surname={employee.surname}
                      imageUrl={employee.thumbnailUrl}
                      size={24}
                    />
                    <span className="tdv-chip-name">{personName(employee)}</span>
                    <PlusOutlined className="tdv-chip-plus" aria-hidden />
                  </button>
                ))}
              </div>
            )}
          </SectionCard>
        </Col>
      </Row>

      <Modal
        title={t('tasks.assignForDay')}
        open={assignOpen}
        onCancel={() => setAssignOpen(false)}
        onOk={handleAssign}
        okText={t('tasks.assignTask')}
        cancelText={t('common.cancel')}
        okButtonProps={{ disabled: !assignEmployee || !assignProject, loading: assigning }}
        destroyOnClose
      >
        <div className="tdv-assign-day">
          <CalendarOutlined /> {current.toDate().toLocaleDateString(locale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </div>
        <label className="tdv-field-label" htmlFor="tdv-assign-employee">
          {t('tasks.selectEmployee')}
        </label>
        <Select
          id="tdv-assign-employee"
          showSearch
          optionFilterProp="label"
          placeholder={t('tasks.selectEmployee')}
          value={assignEmployee}
          onChange={(value) => {
            setAssignEmployee(value);
            setAssignProject(undefined);
          }}
          style={{ width: '100%' }}
          options={employeeOptions}
          optionRender={(option) => (
            <div>
              <div>{option.data.label}</div>
              {option.data.already ? <div className="tdv-option-sub">{option.data.already}</div> : null}
            </div>
          )}
        />
        {assignEmployee && projectsOf(assignEmployee).length > 0 ? (
          <div className="tdv-field-hint">
            {t('tasks.alreadyOn', {
              projects: projectsOf(assignEmployee)
                .map((assignment) => assignment.project?.name ?? '—')
                .join(', '),
            })}
          </div>
        ) : null}
        <label className="tdv-field-label" htmlFor="tdv-assign-project">
          {t('tasks.selectProject')}
        </label>
        <Select
          id="tdv-assign-project"
          showSearch
          optionFilterProp="label"
          placeholder={t('tasks.selectProject')}
          value={assignProject}
          onChange={setAssignProject}
          disabled={!assignEmployee}
          style={{ width: '100%' }}
          options={projectOptions}
          notFoundContent={t('tasks.noProjectsLeft')}
        />
      </Modal>
    </>
  );
};

export default TasksDayView;
